import React, { useState, useRef } from 'react';
import { Dimensions, Platform, ScrollView, TouchableOpacity, View } from 'react-native';

import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import {
  isEmpty as _isEmpty,
  get as _get,
  isFunction as _isFunction,
  isNil as _isNil,
} from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import { VFScreen, VFText, Icon, ErrorScreen } from '@vfgroup-oneplatform/foundation/Components';
import ManualRefresh from '@vfgroup-oneplatform/foundation/Components/ManualRefresh';
import { useTheme, Images } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { findAndGet } from '@vfgroup-oneplatform/framework/Services/Utils/Utils';

import { ConfirmationModalJourney } from '../../../../Common/UI';
import { PRODUCT_STATUS } from '../../../../Services/Enums';
import { getThemeImages } from '../../../../Themes';
import SohoFrameworkTestKeys from '../../../../Utils/SohoFrameworkTestKeys';
import { CONTRACT_DATA_TYPES } from '../../../Utils/Enums';
import PlanTitleLoading from '../../Components/PlanTitleLoading';
import { PlanWithExtrasModal } from '../../Containers';
import UsersContainer from '../../Containers/Users/Users';
import { QUICK_ACTION_MENU_STEP, QUICK_ACTION_MENU_CONFIG } from '../PlanUsers/PlanUsers.Enum';

import styles from './Plan.Styles';

const isWithUpgradePlanButton = (contractData, isInactivePlan) => {
  if (!isInactivePlan) {
    const eligibilityDate = moment(
      findAndGet(contractData, 'type', CONTRACT_DATA_TYPES.ELIGIBLE_UPGRADE_FROM, 'value', ''),
      'DD/MM/YYYY',
    );
    if (moment().isAfter(eligibilityDate)) {
      return true;
    }
  }

  return false;
};

function Plan({
  vfScreenProps,
  withTray,
  plan,
  isLoading,
  error,
  images,
  title,
  onRemovePress,
  onViewProfilePress,
  onAddUsagePress,
  onAddUsageConfirmationPress,
  addExtraPlanData,
  onAddUsageClose,
  onReturnToBusinessOverviewPress,
  onTryAgainPress,
  withRemoveUser,
  onExtrasErrorPrimaryButtonPress,
  shouldShowAddUsageButton,
  isCurrencyFirst,
  onRefreshPress,
  ...props
}) {
  const SUB_HEADER_MAX_HEIGHT = 92;
  const { users, name: planName } = plan;
  const theme = useTheme();
  const scrollViewRef = useRef(undefined);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [isHeaderLoading, setIsHeaderLoading] = useState(false);
  const CMJRef = useRef(undefined);
  const isInactivePlan = plan.status === PRODUCT_STATUS.TERMINATED;
  const isError = !_isNil(error);

  const _onReturnToBusinessOverviewPress = () => {
    if (_isFunction(onReturnToBusinessOverviewPress)) {
      onReturnToBusinessOverviewPress();
    }
  };
  const _onReturnToPlanPress = () => {
    const { onReturnToPlanPress } = props;
    scrollViewRef.current.scrollTo({
      y: 0,
      animated: false,
    });
    if (_isFunction(onReturnToPlanPress)) {
      onReturnToPlanPress();
    }
  };
  const renderAddUsersButton = () => {
    const {
      onAddUserPress,
      addUserIcon,
      addUserContainerStyle,
      addUserTextStyle,
      addUserIconStyle,
      withAddUser,
    } = props;
    const SohoImages = getThemeImages(theme.name);
    return (
      !isInactivePlan &&
      withAddUser && (
        <TouchableOpacity
          style={[styles.addUserContainerStyle, addUserContainerStyle]}
          onPress={() => {
            onAddUserPress(plan);
          }}
          testID={SohoFrameworkTestKeys.PlanAddUsersBtn}
        >
          <VFText
            i18nKey={'soho_plans_plan_add_user_label'}
            style={[styles.addUserTextStyle(theme), addUserTextStyle]}
          />
          <Icon
            size={20}
            name={addUserIcon ? images?.[addUserIcon] : SohoImages.ic_users_new}
            style={[styles.addUserIconStyle, addUserIconStyle]}
          />
        </TouchableOpacity>
      )
    );
  };
  const renderError = () => {
    return (
      <ErrorScreen
        iconStyle={styles.errorIconStyle}
        text={error?.message}
        {...error}
        onPress={onTryAgainPress}
        textButton={'soho_plans_plan_error_try_again'}
      />
    );
  };
  const _onRemoveUserPress = user => {
    CMJRef.current.setConfigs({
      confirmationQuickActionConfig:
        QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.CONFIRM],
      loadingScreenConfig: QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.LOADING],
      successConfigScreen: {
        ...QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.SUCCESS],
        descriptionPlaceHolders: [planName],
        primaryButtonProps: {
          ...QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.SUCCESS].primaryButtonProps,
          onPress: _onReturnToPlanPress,
        },
        secondaryButtonProps: {
          ...QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.SUCCESS].secondaryButtonProps,
          onPress: _onReturnToBusinessOverviewPress,
        },
      },
      failureConfigScreen: QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.FAILURE],
      dataToBeSent: {
        user,
      },
    });
    CMJRef.current.openQAM();
  };
  const planWithExtrasModalProps = () => {
    return {
      ...plan,
      title: 'soho_plans_plan_card_title',
      isLoading: isLoading,
      containerStyle: isLoading ? styles.loadingContainerStyle : undefined,
      renderHeaderRightSection: renderAddUsersButton,
      images: images,
      onAddUsagePress: onAddUsagePress,
      addExtraPlanData: addExtraPlanData,
      onAddUsageClose: onAddUsageClose,
      onAddUsageConfirmationPress: onAddUsageConfirmationPress,
      usageValuesTextStyle: styles.usageValuesTextStyle,
      withPercentageView: false,
      withViewPlanButton: false,
      withInformationIcon: false,
      withUpgradePlanButton: isWithUpgradePlanButton(
        _get(plan, 'contractData', []),
        isInactivePlan,
      ),
      plansExtrasErrorScreenProps: {
        title: 'soho_extras_modal_error_screen_title',
        onPrimaryBtnPress: onExtrasErrorPrimaryButtonPress,
        primaryBtnTitle: 'soho_extras_modal_error_screen_primary_button_title',
      },
      ...props,
      shouldShowAddUsageButton: !isInactivePlan && shouldShowAddUsageButton,
    };
  };
  const usersContainerProps = () => {
    return {
      users: users,
      images: images,
      onViewProfilePress: onViewProfilePress,
      titleContainerStyle: styles.usersTitleContainerStyle,
      titleTextStyle: styles.usersTitleTextStyle,
      onRemoveUserPress: user => _onRemoveUserPress(user),
      ...props,
      withRemoveUser: !isInactivePlan && withRemoveUser,
    };
  };
  const scrollViewProps = () => {
    return {
      ref: scrollViewRef,
      showsVerticalScrollIndicator: false,
      scrollEnabled: !isLoading,
      contentContainerStyle: [
        withTray && styles.scrollViewContentContainerWithTray,
        styles.scrollViewContentContainer,
      ],
    };
  };
  const renderTitleLoading = () => {
    return <PlanTitleLoading withRightView={true} />;
  };
  const renderContent = () => {
    if (isError) {
      return renderError();
    }

    const isUserContainerVisible = !_isEmpty(users) && !isLoading;

    return (
      <ScrollView  {...scrollViewProps()}>
        {isLoading ? (
          renderTitleLoading()
        ) : (
          <View style={styles.titleContainerStyle}>
            <VFText i18nKey={title} style={styles.titleTextStyle} />
            {renderAddUsersButton()}
          </View>
        )}
        <PlanWithExtrasModal {...planWithExtrasModalProps()} />
        {isUserContainerVisible && <UsersContainer {...usersContainerProps()} />}
      </ScrollView>
    );
  };

  const onRefreshButtonPress = async () => {
    scrollViewRef.current.scrollTo({
      y: 0,
      animated: false,
    });
    setIsHeaderLoading(true);
    try {
      await onRefreshPress();
      return Promise.resolve(true);
    } catch (e) {
      return Promise.resolve(false);
    } finally {
      setIsHeaderLoading(false);
    }
  };

  const renderManualRefresh = () => {
    return (
      <ManualRefresh
        onRefresh={onRefreshButtonPress}
        images={Images}
        containerStyle={styles.manualRefreshContainerStyle}
        lastUpdateTime={lastUpdateTime}
        setLastUpdateTime={() => setLastUpdateTime(new Date())}
        refreshTitleStatusKeys={{
          updating: 'soho_plans_refresh_header_button_updating',
          updatedNow: 'soho_plans_refresh_header_button_updated_just_now',
          sinceLastUpdate: 'soho_plans_refresh_header_button_updated_min_ago',
        }}
      />
    );
  };

  const getVFScreenProps = () => {
    const { price, currency, contractData } = plan;

    const resetDays = moment(
      findAndGet(contractData, 'type', CONTRACT_DATA_TYPES.RENEWAL, 'value', ''),
      'DD/MM/YYYY',
    );

    const daysDifference = resetDays?.diff(moment(), 'days');

    const { onClose, onBack } = vfScreenProps;
    const currencyWithPrice = isCurrencyFirst ? currency + price : price + currency;
    return {
      withHeader: true,
      withSubHeader: false,
      withHeaderSpace: false,
      isHeaderLoading,
      headerShimmerStyle: {
        secondLineStyle: styles.secondLineStyle,
        secondLineContainerStyle: styles.secondLineContainerStyle,
      },
      onClose,
      title: 'soho_plans_title',
      onBack,
      titleStyle: styles.vfScreenTitleStyle,
      subHeaderTitle: 'soho_plans_sub_header_title',
      subHeaderTitleContainerStyle: styles.subHeaderTitleContainerStyle,
      subHeaderTitlePlaceholders: users ? [`${users?.length}`] : [''],
      subHeaderSubTitle: currencyWithPrice,
      subHeaderAfterSubTitle: 'soho_plans_sub_header_description_title',
      subHeaderAfterSubTitleStyle: styles.subHeaderAfterSubTitleStyle,
      subHeaderDescription:
        daysDifference > 1
          ? 'soho_plans_plan_header_reset_in_days'
          : 'soho_plans_plan_header_reset_in_day',
      subHeaderDescriptionPlaceholders: [daysDifference],
      subHeaderDescriptionStyle: styles.subHeaderDescriptionStyle,
      renderDescriptionInfo: renderManualRefresh,
      subHeaderMaxHeight: SUB_HEADER_MAX_HEIGHT,
      ...vfScreenProps,
    };
  };
  return (
    <VFScreen  {...getVFScreenProps()}>
      {renderContent()}
      <ConfirmationModalJourney ref={CMJRef} functionToBeExecuted={onRemovePress} />
    </VFScreen>
  );
}

Plan.defaultProps = {
  plan: {},
  withAddUser: true,
  withRemoveUser: true,
  withPriceVat: false,
  title: 'soho_plan_single_plan_title',
};

Plan.propTypes = {
  vfScreenProps: PropTypes.objectOf({
    onClose: PropTypes.func,
    onBack: PropTypes.func,
    showBack: PropTypes.bool,
  }),
  title: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  withTray: PropTypes.bool,
  withInformationIcon: PropTypes.bool,
  images: PropTypes.object,
  onAddUserPress: PropTypes.func,
  onRemovePress: PropTypes.func,
  onViewProfilePress: PropTypes.func,
  onAddUsagePress: PropTypes.func,
  onAddUsageConfirmationPress: PropTypes.func,
  addUserIcon: PropTypes.string,
  addUserContainerStyle: ViewPropTypes.style,
  addUserTextStyle: TextPropTypes.style,
  addUserIconStyle: Icon.propTypes.style,
  plan: PropTypes.objectOf({
    type: PropTypes.string,
    serviceType: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    renewalDate: PropTypes.string,
    usersIcon: PropTypes.string,
    withPercentageView: PropTypes.bool,
    usageData: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        usageType: PropTypes.string,
        icon: PropTypes.string,
        isUnlimited: PropTypes.bool,
        totalAmount: PropTypes.number,
        totalUnits: PropTypes.string,
        usedAmount: PropTypes.number,
        usedUnits: PropTypes.string,
        valuePercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    isCurrencyFirst: PropTypes.bool,
    withPrice: PropTypes.bool,
    withPriceVat: PropTypes.bool,
    contractData: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.string,
        type: PropTypes.string,
      }),
    ),
    users: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        role: PropTypes.string,
        name: PropTypes.string,
        '@type': PropTypes.string,
        '@referredType': PropTypes.string,
      }),
    ),
    unassignedUsers: PropTypes.arrayOf(
      PropTypes.shape({
        '@type': PropTypes.string,
        name: PropTypes.string,
        id: PropTypes.string,
        role: PropTypes.string,
      }),
    ),
    withViewPlanButton: PropTypes.bool,
    withUpgradePlanButton: PropTypes.bool,
    onViewPlanPress: PropTypes.func,
    onUpgradePlanPress: PropTypes.func,
    percentageIndicatorFillColor: PropTypes.string,
    percentageIndicatorSecondaryColor: PropTypes.string,
    percentageIndicatorShouldAnimate: PropTypes.bool,
    images: PropTypes.object,
    containerStyle: ViewPropTypes.style,
    cardContainerStyle: ViewPropTypes.style,
    titleContainerStyle: ViewPropTypes.style,
    titleTextStyle: TextPropTypes.style,
    nameTextStyle: TextPropTypes.style,
    statusTextStyle: TextPropTypes.style,
    renewalDateTextStyle: TextPropTypes.style,
    numberOfUsersTextStyle: TextPropTypes.style,
    usagePercentageContainerStyle: ViewPropTypes.style,
    percentageIndicatorContainerStyle: ViewPropTypes.style,
    contractDataContainerStyle: ViewPropTypes.style,
    usageValuesTextStyle: TextPropTypes.style,
  }),
  isCurrencyFirst: PropTypes.bool,
  addExtraPlanData: PropTypes.object,
  onAddUsageClose: PropTypes.func,
  onReturnToBusinessOverviewPress: PropTypes.func,
  onTryAgainPress: PropTypes.func,
  onRefreshPress: PropTypes.func,
  onReturnToPlanPress: PropTypes.func,
  withAddUser: PropTypes.bool,
  withRemoveUser: PropTypes.bool,
  shouldShowAddUsageButton: PropTypes.bool,
  onExtrasErrorPrimaryButtonPress: PropTypes.func,
};

export default Plan;
